import { countPlural, Destroyer } from '@adaptiva/core/common';
import { ErrorHandlerService } from '@adaptiva/core/error-handler';
import { Licensing } from '@adaptiva/core/licensing';
import { SnackbarService } from '@adaptiva/core/snackbars';
import { GlobalPermissions, GlobalUiPermissionsService, LicenseManagerService } from '@adaptiva/data-service';
import { computed, inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { EMPTY, map, Observable, tap, zip } from 'rxjs';
import { IProductTrialDisplay } from './interfaces';

@Injectable()
export class LlmFreeTrialComponentService extends Destroyer {

	protected readonly snackbars: SnackbarService = inject(SnackbarService);

	protected readonly globalPermissions: GlobalUiPermissionsService = inject(GlobalUiPermissionsService);

	protected readonly errorHandler: ErrorHandlerService = inject(ErrorHandlerService);

	protected readonly licenseManager: LicenseManagerService = inject(LicenseManagerService);

	protected readonly productToSkuMappings: Signal<Record<string, Record<string, string>>> = toSignal(this.licenseManager.getAiSkuMappings());

	protected readonly licenseStates: Signal<Map<string, Licensing.States>> = toSignal(this.licenseManager.licenseStates);

	public readonly productList: Signal<IProductTrialDisplay[]> = computed(() => {
		const _mappings: Record<string, Record<string, string>> = this.productToSkuMappings();

		if (_mappings == null)
			return [];

		const _states: Map<string, Licensing.States> = this.licenseStates();

		return Object.entries(_mappings)
			.reduce((_list: IProductTrialDisplay[], [_product, _skus]: [string, Record<string, string>]) => {
				const _state: Licensing.States = _states.get(_product);

				Object.entries(_skus).forEach(([_sku, _description]: [string, string]) => {
					_list.push({
						productName: _product,
						skuName: _sku,
						skuDescription: _description,
						state: _state ?? Licensing.States.NOT_PRESENT,
						isActive: false
					});
				});

				return _list;
			}, []);
	});

	public readonly hasPermissions: Signal<boolean> = toSignal(
		this.globalPermissions.checkPermission(GlobalPermissions.PERMISSIONS_FOR_CHANGE_LICENSING_STATE)
	);

	public submit(_skus: string[]): Observable<void> {
		const _evals: Observable<void>[] = _skus?.map((_sku: string) => this.licenseManager.addTrialProduct(_sku)) ?? [];

		if (!_evals.length) {
			this.errorHandler.defaultErrorHandler(
				new Error('No trial products selected'),
				'LlmFreeTrialComponentService.submit',
				true,
				'Invalid Selection'
			);

			return EMPTY;
		}

		return zip(_evals).pipe(
			this.takeOneUntilDestroyed(),
			map(() => void 0),
			tap({
				next: (): void => {
					this.snackbars.display(`Successfully started ${countPlural(_evals, 'trial', 'trials')}`);
				},
				error: (e: Error): void => {
					this.errorHandler.defaultErrorHandler(e, 'LlmFreeTrialComponentService.submit');
				}
			}),
		);
	}

}
